import { useMemo, useState } from "react";
import { UserCheck, Plus, X, Paperclip, ClipboardText, CaretDown, CaretUp } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

const YESNO = [["yes", "Yes"], ["no", "No"]];

const BLANK = {
  ref_name: "", ref_contact: "", relationship: "", years_known: "", source: "phone",
  check_date: new Date().toISOString().slice(0, 10), crosscheck_match: "", not_related: "",
  q_kind_character: "", q_recommend: "", q_red_flag: "", q_most_important: "",
  positive: "", notes: "", summary: "", entry_type: "live",
};

const SCRIPT_STEPS = [
  'Introduce yourself: "Hi, this is [your name] from Silver Home Care. [Applicant] listed you as a reference and gave us permission to reach out. Do you have about five minutes?"',
  "Confirm the basics: how they know the applicant, and for how long. Check it matches what the employee told us — and confirm they are not a family member.",
  'Ask the questions in order and tap the answers as you go: "Do you know them to be kind and of good character?" — "Would you recommend them to care for a vulnerable person in their own home, with little supervision?" — "Is there any reason we should NOT hire them?"',
  'Always end with the open question — this is the one that shows we really talked, not just checked boxes: "What do you want me to know most of all about [applicant]?" Write down their actual words below.',
  "Thank them, mark whether the reference is positive overall, add any comment of your own, and save. Attach a screenshot or email as evidence if you have one — the written record is what counts.",
];

function buildSummary(f) {
  const rel = [f.relationship, f.years_known ? `known ${f.years_known}` : ""].filter(Boolean).join(", ");
  const opener = f.entry_type === "legacy"
    ? `Legacy record transcribed from the previous database — ${f.ref_name || "—"}`
    : `Reference check via ${f.source || "phone"} with ${f.ref_name || "—"}`;
  const bits = [opener + (rel ? ` (${rel})` : "")];
  bits.push(`cross-check vs employee's stated relationship/history: ${f.crosscheck_match === "yes" ? "matches" : "n/a"}`);
  bits.push(`not a family member: ${f.not_related === "yes" ? "confirmed" : "n/a"}`);
  bits.push(`knows them to be kind and of good character: ${f.q_kind_character === "yes" ? "yes" : "n/a"}`);
  bits.push(`would recommend for caring for vulnerable individuals in their own homes under minimal supervision: ${f.q_recommend === "yes" ? "yes" : "n/a"}`);
  bits.push(`gave a reason we should not hire: ${f.q_red_flag === "yes" ? "YES — RED FLAG" : "no"}`);
  if (f.q_most_important) bits.push(`what we should know most of all about the applicant: "${f.q_most_important}"`);
  bits.push(`overall: ${f.positive === "yes" ? "POSITIVE REFERENCE" : "NOT a positive reference"}`);
  if (f.notes) bits.push(`office comment: "${f.notes}"`);
  bits.push(`verified by ${f.conducted_by || "the office"} on ${f.check_date}`);
  return bits.join(". ") + ".";
}

function Field({ label, children, testid }) {
  return (
    <label className="block">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-stone-400">{label}</span>
      <div className="mt-1" data-testid={testid}>{children}</div>
    </label>
  );
}

export function ReferenceCheckCard({ uid, emp, docs, isAdmin, canEdit = false, onChanged }) {
  const canWork = isAdmin || canEdit;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showScript, setShowScript] = useState(true);
  const [summaryTouched, setSummaryTouched] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [file, setFile] = useState(null);

  const checks = useMemo(
    () => [...(emp.employment_records || [])].filter((r) => r.kind === "reference_check").reverse(),
    [emp]
  );
  const positives = checks.filter((r) => (r.form || {}).positive === "yes").length;
  const legacyDocs = useMemo(
    () => (docs || []).filter((d) => /reference/i.test(d.title || "") && !checks.some((r) => r.filename === d.title)),
    [docs, checks]
  );

  const appRefs = ((emp.application || {}).references || {});
  const listedRefs = useMemo(() => {
    const out = [];
    (emp.references || []).forEach((r) => {
      if (r && r.name) out.push({ name: r.name, contact: r.contact || "",
        relationship: r.type || "", years: r.known_for || "", review: r.review || "", source: r.source || "" });
    });
    (appRefs.professional || []).forEach((r) => {
      if (r && r.name) out.push({ name: r.name, contact: [r.phone, r.email].filter(Boolean).join(" · "),
        relationship: r.company ? `Professional — ${r.company}` : "Professional reference",
        years: "", may_contact: r.may_contact, review: "", source: "application" });
    });
    (appRefs.personal || []).forEach((r) => {
      if (r && r.name) out.push({ name: r.name, contact: [r.phone, r.email].filter(Boolean).join(" · "),
        relationship: r.relationship || "Personal reference", years: r.years_acquainted || "", review: "", source: "application" });
    });
    const prof = emp.profile || {};
    ["ref1", "ref2"].forEach((pre) => {
      if (prof[`${pre}_name`]) out.push({ name: prof[`${pre}_name`], contact: prof[`${pre}_contact`] || "",
        relationship: prof[`${pre}_relationship`] || "", years: prof[`${pre}_years_known`] || "", review: "", source: "intake" });
    });
    return out;
  }, [emp]);

  if (!canWork && checks.length === 0) return null;

  const set = (k, v) => setForm((f) => {
    const nf = { ...f, [k]: v };
    if (!summaryTouched && k !== "summary") nf.summary = buildSummary(nf);
    return nf;
  });

  const openForm = () => { setForm(BLANK); setSummaryTouched(false); setFile(null); setShowScript(true); setOpen(true); };

  const applyListed = (r) => setForm((f) => {
    const nf = { ...f, ref_name: r.name, ref_contact: r.contact, relationship: r.relationship, years_known: r.years };
    if (!summaryTouched) nf.summary = buildSummary(nf);
    return nf;
  });

  const save = async () => {
    if (!form.ref_name.trim() || !form.check_date || !form.positive) {
      toast.error("Reference name, date, and the positive/not-positive call are required");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/users/${uid}/employment-records/reference-form`, { ...form });
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/users/${uid}/employment-records/${data.record.id}/attachment`, fd);
      }
      toast.success(form.entry_type === "legacy" ? "Legacy reference documented" : "Reference documented");
      setOpen(false);
      setFile(null);
      setForm(BLANK);
      setSummaryTouched(false);
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const attach = async (rid, f) => {
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      await api.post(`/users/${uid}/employment-records/${rid}/attachment`, fd);
      toast.success("Evidence attached");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const yn = (k, label) => (
    <Field label={label} testid={`ref-${k}`}>
      <div className="flex gap-2">
        {YESNO.map(([v, l]) => (
          <button key={v} type="button" onClick={() => set(k, v)}
            className={`rounded-full border px-3 py-1 text-xs font-semibold ${form[k] === v ? "border-[#4A235A] bg-[#4A235A] text-white" : "border-stone-300 text-stone-500"}`}
            data-testid={`ref-${k}-${v}`}>{l}</button>
        ))}
      </div>
    </Field>
  );

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="references-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="flex items-center gap-2 font-heading text-xl tracking-tight text-stone-900">
            <UserCheck size={20} className="text-primary" /> References
          </p>
          <p className="mt-0.5 text-xs text-stone-500" data-testid="references-progress">
            {positives} of 2 positive references documented — the roster rule
          </p>
        </div>
        {canWork && (
          <button onClick={openForm}
            className="inline-flex items-center gap-1 rounded-full bg-[#4A235A] px-4 py-1.5 text-xs font-semibold text-white hover:bg-stone-900"
            data-testid="reference-add-btn">
            <Plus size={12} weight="bold" /> Document a reference check
          </button>
        )}
      </div>

      {(checks.length > 0 || legacyDocs.length > 0) && (
        <div className="mt-3 overflow-x-auto" data-testid="references-list">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-stone-200 text-left text-[10px] font-semibold uppercase tracking-wider text-stone-400">
                <th className="py-1.5 pr-2">Reference</th>
                <th className="py-1.5 pr-2">Date</th>
                <th className="py-1.5 pr-2">Status</th>
                <th className="py-1.5 pr-2">Taken by</th>
                <th className="py-1.5 pr-2">Summary</th>
                <th className="py-1.5">Link</th>
              </tr>
            </thead>
            <tbody>
              {checks.map((r) => {
                const f = r.form || {};
                return (
                  <tr key={r.id} className="border-b border-stone-100 last:border-0" data-testid={`reference-${r.id}`}>
                    <td className="py-2 pr-2 font-medium text-stone-800">{f.ref_name || "Reference"}{f.relationship ? <span className="block text-[11px] font-normal text-stone-400">{f.relationship}</span> : null}</td>
                    <td className="py-2 pr-2 text-xs text-stone-600">{f.check_date || r.record_date}</td>
                    <td className="py-2 pr-2">
                      <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${f.positive === "yes" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : f.positive === "no" ? "border-[#C0392B]/30 bg-[#C0392B]/5 text-[#C0392B]" : "border-stone-300 text-stone-500"}`}
                        data-testid={`reference-status-${r.id}`}>
                        {f.positive === "yes" ? "positive" : f.positive === "no" ? "not positive" : "documented"}
                      </span>
                      {f.entry_type === "legacy" && (
                        <span className="ml-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700" data-testid={`reference-legacy-badge-${r.id}`}>legacy — transcribed</span>
                      )}
                    </td>
                    <td className="py-2 pr-2 text-xs text-stone-600">{f.conducted_by || r.by || "—"}</td>
                    <td className="max-w-48 py-2 pr-2 text-xs text-stone-500"><span className="line-clamp-2">{f.summary || f.notes || "—"}</span></td>
                    <td className="py-2">
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/users/${uid}/employment-records/${r.id}/pdf`} target="_blank" rel="noreferrer"
                        className="text-[11px] font-semibold text-[#4A235A] hover:underline" data-testid={`reference-pdf-${r.id}`}>PDF</a>
                      {(r.attachments || []).map((a, i) => (
                        <a key={i} href={`${process.env.REACT_APP_BACKEND_URL}/api/users/${uid}/record-attachment?key=${encodeURIComponent(a.key)}`}
                          target="_blank" rel="noreferrer"
                          className="ml-2 inline-flex items-center gap-0.5 text-[11px] font-medium text-stone-500 hover:text-[#4A235A]" data-testid={`reference-evidence-${r.id}-${i}`}>
                          <Paperclip size={10} />{a.name.length > 16 ? a.name.slice(0, 16) + "…" : a.name}
                        </a>
                      ))}
                      {canWork && (
                        <label className="ml-2 inline-flex cursor-pointer items-center gap-0.5 text-[11px] font-medium text-stone-400 hover:text-primary" data-testid={`reference-attach-${r.id}`}>
                          <Paperclip size={10} />
                          <input type="file" className="hidden" onChange={(e) => attach(r.id, e.target.files?.[0])} />
                        </label>
                      )}
                    </td>
                  </tr>
                );
              })}
              {legacyDocs.map((d) => (
                <tr key={d.id} className="border-b border-stone-100 last:border-0 bg-stone-50/50" data-testid={`reference-legacy-${d.id}`}>
                  <td className="py-2 pr-2 text-xs text-stone-500">{d.title}</td>
                  <td className="py-2 pr-2 text-xs text-stone-500">{d.doc_date || (d.title.match(/20\d{2}-\d{2}-\d{2}/) || [""])[0]}</td>
                  <td className="py-2 pr-2">
                    <span className="rounded-full border border-stone-300 px-2 py-0.5 text-[10px] font-semibold text-stone-500">legacy — unverified</span>
                  </td>
                  <td className="py-2 pr-2 text-xs text-stone-400">application</td>
                  <td className="max-w-48 py-2 pr-2 text-xs italic text-stone-400">screenshot from the application — kept as evidence, not a completed check</td>
                  <td className="py-2">
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/employee-file-docs/${d.id}/thumb`} target="_blank" rel="noreferrer"
                      className="text-[11px] font-medium text-stone-500 hover:text-[#4A235A]" data-testid={`reference-legacy-link-${d.id}`}>view</a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {checks.length === 0 && legacyDocs.length === 0 && (
        <p className="mt-3 text-sm text-stone-400">No reference checks documented yet.</p>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="reference-form-modal">
          <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-6 shadow-xl">
            <div className="flex items-start justify-between">
              <div>
                <p className="font-heading text-xl tracking-tight text-[#4A235A]">Document a reference check</p>
                <p className="mt-0.5 text-xs text-stone-500">The script below is the whole job — read it, ask, write down what they say. Attachments (screenshots from the old database, etc.) are optional evidence — never a substitute.</p>
              </div>
              <button onClick={() => setOpen(false)} className="text-stone-400 hover:text-stone-700" data-testid="reference-form-close"><X size={18} /></button>
            </div>

            <label className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50/60 p-3" data-testid="ref-legacy-row">
              <input type="checkbox" checked={form.entry_type === "legacy"} onChange={(e) => set("entry_type", e.target.checked ? "legacy" : "live")}
                className="mt-0.5" data-testid="ref-legacy-toggle" />
              <span className="text-xs text-stone-600">
                <span className="font-semibold text-stone-800">Legacy record — transcribed from the previous database.</span>{" "}
                Copy the information exactly as it appears in the old system. The written record is what counts; attach the old screenshot as evidence if you have one.
              </span>
            </label>

            <div className="mt-3 rounded-lg border border-stone-200 bg-stone-50" data-testid="ref-script-panel">
              <button type="button" onClick={() => setShowScript(!showScript)} className="flex w-full items-center justify-between px-3 py-2 text-left" data-testid="ref-script-toggle">
                <span className="flex items-center gap-2 text-xs font-semibold text-stone-700">
                  <ClipboardText size={14} className="text-primary" /> Reference call script — anyone in the office can do this
                </span>
                {showScript ? <CaretUp size={12} /> : <CaretDown size={12} />}
              </button>
              {showScript && (
                <ol className="space-y-2 px-4 pb-3 text-xs leading-relaxed text-stone-600" data-testid="ref-script-steps">
                  {SCRIPT_STEPS.map((s, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="shrink-0 font-bold text-primary">{i + 1}.</span>
                      <span className={i === 3 ? "font-semibold text-stone-800" : ""}>{s}</span>
                    </li>
                  ))}
                </ol>
              )}
            </div>

            <div className="mt-3 rounded-lg border border-[#4A235A]/20 bg-[#4A235A]/[0.04] p-3" data-testid="ref-listed-panel">
              <p className="text-xs font-semibold text-[#4A235A]">Listed by the employee on their application</p>
              {appRefs.authorized ? (
                <p className="mt-0.5 text-[11px] text-stone-500" data-testid="ref-listed-authorized">Permission to contact references: {appRefs.authorized}</p>
              ) : null}
              {listedRefs.length === 0 ? (
                <p className="mt-1 text-xs italic text-stone-400" data-testid="ref-listed-empty">No reference information on this employee's application — fill in the details manually below.</p>
              ) : (
                <div className="mt-2 space-y-1.5">
                  {listedRefs.map((r, i) => (
                    <div key={i} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-stone-200 bg-white px-3 py-2" data-testid={`ref-listed-${i}`}>
                      <div className="text-xs">
                        <span className="font-semibold text-stone-800">Reference {i + 1} — {r.name}</span>
                        {r.relationship ? <span className="text-stone-500"> — {r.relationship}</span> : null}
                        {r.years ? <span className="text-stone-400"> · {r.years}</span> : null}
                        {r.review ? (
                          <span className={`ml-1.5 rounded-full border px-1.5 py-px text-[10px] font-semibold ${/satisfactory|positive/i.test(r.review) ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-stone-300 text-stone-500"}`}
                            data-testid={`ref-listed-review-${i}`}>{r.review}</span>
                        ) : (
                          <span className="ml-1.5 rounded-full border border-[#B7791F]/40 bg-[#B7791F]/10 px-1.5 py-px text-[10px] font-semibold text-[#8a5d14]"
                            data-testid={`ref-listed-review-${i}`}>listed — not checked yet</span>
                        )}
                        {r.contact ? <span className="block text-stone-500">{r.contact}</span> : null}
                        {r.source ? <span className="block text-[10px] text-stone-400">source: {r.source}</span> : null}
                      </div>
                      <button type="button" onClick={() => applyListed(r)}
                        className="rounded-full border border-[#4A235A]/30 px-3 py-1 text-[11px] font-semibold text-[#4A235A] hover:bg-[#4A235A] hover:text-white"
                        data-testid={`ref-listed-use-${i}`}>Use</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-stone-500" data-testid="ref-check-heading">
              Reference check{form.ref_name.trim() ? ` — ${form.ref_name.trim()}` : ""}
            </p>
            <div className="mt-2 grid gap-3 text-left sm:grid-cols-2">
              <Field label="Reference name *" testid="ref-name"><input value={form.ref_name} onChange={(e) => set("ref_name", e.target.value)} className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm" data-testid="ref-name-input" /></Field>
              <Field label="Phone / email" testid="ref-contact"><input value={form.ref_contact} onChange={(e) => set("ref_contact", e.target.value)} className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm" data-testid="ref-contact-input" /></Field>
              <Field label="Relationship" testid="ref-rel"><input value={form.relationship} onChange={(e) => set("relationship", e.target.value)} placeholder="former supervisor, coworker…" className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm" data-testid="ref-relationship-input" /></Field>
              <Field label="Known how long" testid="ref-years"><input value={form.years_known} onChange={(e) => set("years_known", e.target.value)} placeholder="e.g. 4 years" className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm" data-testid="ref-years-input" /></Field>
              <Field label="How reached" testid="ref-source">
                <select value={form.source} onChange={(e) => set("source", e.target.value)} className="w-full rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm" data-testid="ref-source-select">
                  <option value="phone">Phone</option><option value="email">Email</option><option value="text">Text</option><option value="in person">In person</option>
                </select>
              </Field>
              <Field label="Date of check *" testid="ref-date"><input type="date" value={form.check_date} onChange={(e) => set("check_date", e.target.value)} className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm" data-testid="ref-date-input" /></Field>
              {yn("crosscheck_match", "Matches what the employee told us?")}
              {yn("not_related", "Not a family member?")}
              {yn("q_kind_character", "Kind, good character?")}
              {yn("q_recommend", "Would recommend for caring for vulnerable people at home?")}
              {yn("q_red_flag", "Gave a reason NOT to hire?")}
              <div className="sm:col-span-2">
                <Field label='The open question — "What do you want me to know most of all about the applicant?"' testid="ref-most-important">
                  <textarea value={form.q_most_important} onChange={(e) => set("q_most_important", e.target.value)} rows={2}
                    placeholder="Write down their actual words…"
                    className="w-full rounded-lg border border-[#4A235A]/30 bg-[#4A235A]/[0.03] px-3 py-2 text-sm" data-testid="ref-most-important-input" />
                </Field>
              </div>
              <div className="sm:col-span-2">
                {yn("positive", "Overall: positive reference? *")}
              </div>
              <div className="sm:col-span-2">
                <Field label="Your comment (optional — woven into the summary below)" testid="ref-comment">
                  <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm" data-testid="ref-comment-input" />
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Summary (auto-written from the answers — edit if you like)" testid="ref-summary">
                  <textarea value={form.summary} onChange={(e) => { setSummaryTouched(true); set("summary", e.target.value); }} rows={4}
                    className="w-full rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 text-xs leading-relaxed text-stone-600" data-testid="ref-summary-input" />
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Attach evidence (optional — screenshots from the old database, emails, etc.)" testid="ref-file">
                  <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-xs" data-testid="ref-file-input" />
                </Field>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <button onClick={save} disabled={busy}
                className="flex-1 rounded-full bg-[#4A235A] px-5 py-2 text-sm font-semibold text-white hover:bg-stone-900 disabled:opacity-50"
                data-testid="reference-save">{busy ? "Saving…" : form.entry_type === "legacy" ? "Save legacy record" : "Save reference check"}</button>
              <button onClick={() => setOpen(false)} className="rounded-full border border-stone-300 px-5 py-2 text-sm text-stone-600 hover:bg-stone-50" data-testid="reference-cancel">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
